<template>
  <div id="app" class="app-shell">
    <header class="app-header">
      <div class="app-logo">
        <RouterLink to="/" class="logo-link">Honest Pitches</RouterLink>
      </div>
      <nav class="app-nav" aria-label="Main navigation">
        <RouterLink to="/" class="nav-link" :class="{ active: route.name === 'dashboard' }">
          My Pitches
        </RouterLink>
        <RouterLink to="/pitches/new" class="nav-link">
          <span aria-hidden="true">+</span> New Pitch
        </RouterLink>
        <RouterLink to="/billing" class="nav-link" :class="{ active: route.name === 'billing' }">
          Billing
        </RouterLink>
        <RouterLink to="/settings" class="nav-link" :class="{ active: route.name === 'settings' }">
          Settings
        </RouterLink>
        <RouterLink
          to="/agents/activity"
          class="nav-link"
          :class="{ active: route.name === 'agents-activity' }"
        >
          Agents
        </RouterLink>
        <button @click="logout" class="nav-link nav-logout" :disabled="auth.loading">
          {{ auth.loading ? "…" : "Sign out" }}
        </button>
      </nav>
    </header>

    <main class="app-main">
      <RouterView />
    </main>
  </div>
</template>

<script setup lang="ts">
import { RouterLink, RouterView, useRoute, useRouter } from "vue-router";
import { useAuthStore } from "./lib/auth";

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();

async function logout() {
  await auth.logout();
  router.push({ name: "auth" });
}
</script>

<style>
/* ─── Global reset & tokens ─────────────────────────────────────────── */
:root {
  --hp-bg: #f9fafb;
  --hp-surface: #ffffff;
  --hp-border: #e5e7eb;
  --hp-text: #111827;
  --hp-text-muted: #6b7280;
  --hp-primary: #2563eb;
  --hp-danger: #dc2626;
  --hp-success: #16a34a;
  --hp-hype: #f59e0b;
  --hp-reality: #10b981;
}

* { box-sizing: border-box; }

body {
  margin: 0;
  background: var(--hp-bg);
  color: var(--hp-text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* ─── App shell ────────────────────────────────────────────────────── */
.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  background: var(--hp-surface);
  border-bottom: 1px solid var(--hp-border);
  padding: 0 1.5rem;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
}

.app-logo .logo-link {
  font-weight: 800;
  font-size: 1rem;
  color: var(--hp-text);
  text-decoration: none;
  letter-spacing: -0.02em;
}

.app-nav {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.nav-link {
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--hp-text-muted);
  transition: background 0.1s, color 0.1s;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
}

.nav-link:hover,
.nav-link.active {
  background: var(--hp-bg);
  color: var(--hp-text);
}

.nav-logout {
  color: var(--hp-danger);
}
.nav-logout:hover {
  background: #fef2f2;
  color: var(--hp-danger);
}

.app-main {
  flex: 1;
}
</style>
